import React, { useState } from 'react';
import { Search, IdCard, User, Mail, ArrowRight } from 'lucide-react';
import logo from "../images/logo.png";
import AutoCadastroService from '../../modules/services/AutoCadastroService';
import { formatCNPJ } from '../../utils/format';
import '../css/login.css';

const SENHA_ETAPA_BUSCA = 'busca';
const SENHA_ETAPA_JA_EXISTE = 'ja-existe';
const SENHA_ETAPA_CADASTRO = 'cadastro';
const SENHA_ETAPA_CONCLUIDO = 'concluido';

function CadastroMaster() {
    const [etapa, setEtapa] = useState(SENHA_ETAPA_BUSCA);
    const [codigoCliente, setCodigoCliente] = useState('');
    const [cnpj, setCnpj] = useState('');
    const [cliente, setCliente] = useState(null);
    const [masterExistente, setMasterExistente] = useState(null);
    const [nmUsuario, setNmUsuario] = useState('');
    const [dsEmailSugerido, setDsEmailSugerido] = useState('');
    const [carregando, setCarregando] = useState(false);
    const [erro, setErro] = useState('');
    const [mensagemFinal, setMensagemFinal] = useState('');

    const handleBuscar = async (event) => {
        event.preventDefault();
        if (!codigoCliente.trim() || !cnpj.trim()) {
            setErro('Informe o código do cliente e o CNPJ.');
            return;
        }

        setErro('');
        setCarregando(true);
        try {
            const resultado = await AutoCadastroService.verificarCliente(codigoCliente.trim(), cnpj.trim());
            setCliente(resultado);
            if (resultado.podeCadastrar) {
                // Pré-preenche com o nome do cliente. O e-mail do gestor vem mascarado da API
                // (ex.: "jo***@dominio.com") — é só uma dica pro usuário reconhecer o próprio
                // e-mail, nunca preenche o campo sozinho: o usuário sempre digita o e-mail
                // completo de próprio punho.
                setNmUsuario(resultado.nmCliente || '');
                setDsEmailSugerido(resultado.dsEmailGestor || '');
                setEtapa(SENHA_ETAPA_CADASTRO);
            } else {
                setMasterExistente({ nome: resultado.nmMasterExistente, email: resultado.dsEmailMasterExistente });
                setEtapa(SENHA_ETAPA_JA_EXISTE);
            }
        } catch (error) {
            setErro(error.message || 'Não foi possível verificar o cliente.');
        } finally {
            setCarregando(false);
        }
    };

    // Quando já existe um e-mail de contato cadastrado pra esse cliente, o usuário só confirma
    // que é o dele — nunca digita, o backend usa sempre esse e-mail (não um valor digitado).
    const temEmailCadastrado = !!dsEmailSugerido;

    const handleConfirmarCadastro = async () => {
        setErro('');
        setCarregando(true);
        try {
            const mensagem = await AutoCadastroService.cadastrarMaster({
                codigoCliente: codigoCliente.trim(),
                cnpjCpf: cnpj.trim(),
                nmUsuario: nmUsuario.trim(),
            });
            setMensagemFinal(mensagem);
            setEtapa(SENHA_ETAPA_CONCLUIDO);
        } catch (error) {
            setErro(error.message || 'Erro ao cadastrar usuário master.');
        } finally {
            setCarregando(false);
        }
    };

    return (
        <main className="login-page">
            <aside className="login-brand-panel">
                <div className="login-brand-mark">
                    <span className="login-wordmark"> 
                        <img src={logo} alt="Logo" className="logo" />
                    </span>
                </div>
                <div className="login-brand-headline">
                    <h1>Cadastre-se no portal.</h1>
                    <p>Crie o acesso master do seu cliente para gerenciar usuários e permissões.</p>
                </div>
            </aside>

            <section className="login-form-panel">
                <div className="login-form-wrap">
                    {etapa === SENHA_ETAPA_BUSCA && (
                        <>
                            <div className="login-form-header">
                                <h2>Identifique seu cliente</h2>
                                <p>Informe o código do cliente e o CNPJ para continuar.</p>
                            </div>

                            <form className="login-form" onSubmit={handleBuscar} noValidate>
                                <div className="login-field">
                                    <label htmlFor="codigo-cliente">Código do cliente</label>
                                    <div className="login-input-wrap">
                                        <Search className="login-ico-lead" size={18} />
                                        <input
                                            id="codigo-cliente"
                                            type="text"
                                            value={codigoCliente}
                                            onChange={(e) => setCodigoCliente(e.target.value)}
                                            disabled={carregando}
                                        />
                                    </div>
                                </div>

                                <div className="login-field">
                                    <label htmlFor="cnpj-cliente">CNPJ</label>
                                    <div className="login-input-wrap">
                                        <IdCard className="login-ico-lead" size={18} />
                                        <input
                                            id="cnpj-cliente"
                                            type="text"
                                            value={cnpj}
                                            onChange={(e) => setCnpj(e.target.value)}
                                            onBlur={() => setCnpj((atual) => (atual.trim() ? formatCNPJ(atual) : atual))}
                                            disabled={carregando}
                                        />
                                    </div>
                                </div>

                                {erro && <div className="login-erro">{erro}</div>}

                                <button type="submit" className="login-btn-submit" disabled={carregando}>
                                    {carregando ? 'Verificando...' : 'Continuar'}
                                    <ArrowRight size={16} />
                                </button>
                            </form>
                        </>
                    )}

                    {etapa === SENHA_ETAPA_JA_EXISTE && (
                        <>
                            <div className="login-form-header">
                                <h2>Este cliente já tem um master</h2>
                                <p>
                                    {cliente?.nmCliente} já possui um usuário master cadastrado. Solicite acesso
                                    secundário diretamente com ele:
                                </p>
                            </div>
                            <p><strong>Nome:</strong> {masterExistente?.nome}</p>
                            <p><strong>E-mail:</strong> {masterExistente?.email}</p>
                            <button type="button" className="login-btn-submit" onClick={() => setEtapa(SENHA_ETAPA_BUSCA)}>
                                Voltar
                            </button>
                        </>
                    )}

                    {etapa === SENHA_ETAPA_CADASTRO && temEmailCadastrado && (
                        <>
                            <div className="login-form-header">
                                <h2>Seus dados</h2>
                                <p>
                                    {cliente?.nmCliente} ainda não tem um usuário master. Confirme seus dados para
                                    criar o acesso.
                                </p>
                            </div>

                            <div className="login-form">
                                <div className="login-field">
                                    <label>Nome completo</label>
                                    <div className="login-input-wrap">
                                        <User className="login-ico-lead" size={18} />
                                        <input type="text" value={nmUsuario} disabled={true} readOnly />
                                    </div>
                                </div>

                                <div className="login-field">
                                    <label>E-mail</label>
                                    <div className="login-input-wrap">
                                        <Mail className="login-ico-lead" size={18} />
                                        <input type="text" value={dsEmailSugerido} disabled={true} readOnly />
                                    </div>
                                    <small className="login-campo-ajuda">
                                        Este é o e-mail de contato já cadastrado para este cliente. Se não for o
                                        seu, ou quiser alterá-lo, entre em contato pelo{' '}
                                        <a href="mailto:cadastro@gam.com.br">cadastro@gam.com.br</a> e solicite a
                                        atualização do seu cadastro.
                                    </small>
                                </div>

                                {erro && <div className="login-erro">{erro}</div>}

                                <button
                                    type="button"
                                    className="login-btn-submit"
                                    disabled={carregando}
                                    onClick={handleConfirmarCadastro}
                                >
                                    {carregando ? 'Cadastrando...' : 'Sim, é meu e-mail — Cadastrar'}
                                    <ArrowRight size={16} />
                                </button>
                            </div>
                        </>
                    )}

                    {etapa === SENHA_ETAPA_CADASTRO && !temEmailCadastrado && (
                        <>
                            <div className="login-form-header">
                                <h2>Não encontramos um e-mail de contato</h2>
                                <p>
                                    {cliente?.nmCliente} ainda não tem um e-mail de contato (Gestor) cadastrado —
                                    por isso não é possível criar o usuário master por aqui ainda.
                                </p>
                            </div>
                            <p>
                                Entre em contato pelo{' '}
                                <a href="mailto:cadastro@gam.com.br">cadastro@gam.com.br</a> para que a gente
                                cadastre esse e-mail. Depois disso, você já consegue voltar aqui e concluir o
                                cadastro do seu usuário master.
                            </p>
                            <button type="button" className="login-btn-submit" onClick={() => setEtapa(SENHA_ETAPA_BUSCA)}>
                                Voltar
                            </button>
                        </>
                    )}

                    {etapa === SENHA_ETAPA_CONCLUIDO && (
                        <>
                            <div className="login-form-header">
                                <h2>Verifique seu e-mail</h2>
                                <p>{mensagemFinal}</p>
                            </div>
                        </>
                    )}

                    <div className="login-help-block">
                        <a href="/">Voltar para o login</a>
                    </div>
                </div>
            </section>
        </main>
    );
}

export default CadastroMaster;
